import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { USER_ADDRESS_MENTION_REGEX } from '@components/RichTextPlate/utils/remark/remarkTransformUserAndChannels'
import { FullyReadMarker } from '@towns-protocol/proto'
import { create } from 'zustand'

import { isTownsAppUrl } from '../../../utils/isTownsAppUrl'
import { ListItem } from '../types'

import { ScrollbackState } from './useScrollback'

/** Global state for focusing on a message in the message timeline.
 * @desc Keep in mind that the message will only be focused if you're in the MessageTimeline where the message is.
 */
export const useFocusMessage = create<{
    focusItem: { type: 'new' | 'forced'; id: string; _seq: number }
    actions: {
        setFocusItem: (id: string, type: 'new' | 'forced') => void
        refocusItem: (id: string, type: 'new' | 'forced') => void
    }
}>((set, get) => ({
    focusItem: { type: 'new', id: '', _seq: 0 },
    actions: {
        setFocusItem: (id, type) => set({ focusItem: { id, type, _seq: get().focusItem._seq } }),
        // refocusItem increments _seq to ensure re-render even when clicking same item twice
        refocusItem: (id, type) => set({ focusItem: { id, type, _seq: get().focusItem._seq + 1 } }),
    },
}))

export const useFocusItem = (
    listItems: ListItem[],
    highlightId: string | undefined,
    userId: string | undefined,
    fullyreadMarker: FullyReadMarker | undefined,
) => {
    // there's no need to focus on a message that doesn't exist
    const validHighlightId = listItems.find((e) => e.key === highlightId)?.key

    const storeFocus = useFocusMessage((state) => state.focusItem)
    const last = listItems[listItems.length - 1]

    const lastKey = last?.key

    const [focusItem, setFocusItem] = useState<FocusOption | undefined>(() =>
        validHighlightId
            ? {
                  key: validHighlightId,
                  align: 'start' as const,
                  sticky: true,
                  force: true,
              }
            : {
                  key: lastKey,
                  align: 'end' as const,
              },
    )

    useLayoutEffect(() => {
        // Skip if no id (initial state)
        if (!storeFocus.id) {
            return
        }

        const isForced = storeFocus.type === 'forced'
        const newFocusItem: FocusOption = {
            key: storeFocus.id,
            align: isForced ? 'start' : 'end',
            sticky: true,
            force: isForced,
            margin: isForced ? 50 : 0,
        }
        setFocusItem(newFocusItem)
        // Explicitly depend on _seq to ensure re-runs on repeated refocus of same item
    }, [storeFocus.id, storeFocus.type, storeFocus._seq, storeFocus])

    const force =
        last?.type === 'message' &&
        last.item.event.sender.id === userId &&
        last.item.event.isLocalPending

    useLayoutEffect(() => {
        const newFocusItem = {
            key: lastKey,
            align: 'end' as const,
            force,
        }
        setFocusItem(newFocusItem)
    }, [lastKey, force])

    const fullyreadMarkerFocusedOnceRef = useRef(false)

    useLayoutEffect(() => {
        if (fullyreadMarker?.isUnread && !fullyreadMarkerFocusedOnceRef.current) {
            fullyreadMarkerFocusedOnceRef.current = true
            const newFocusItem = {
                key: fullyreadMarker.eventId,
                align: 'start' as const,
                sticky: true,
                force: true,
                margin: 50,
            }
            setFocusItem(newFocusItem)
        }
    }, [fullyreadMarker])

    useLayoutEffect(() => {
        if (validHighlightId) {
            const newFocusItem = {
                key: validHighlightId,
                align: 'start' as const,
                sticky: true,
                force: true,
                margin: 50,
            }
            setFocusItem(newFocusItem)
        }
    }, [validHighlightId])

    return { focusItem }
}

/**
 * if a message is highlighted (via anchor link or open thread) we get it into
 * the viewport and may not to apply scrollbacks to achieve this
 */
export const useScrollbackToFocusItem = (
    highlightId: string | undefined,
    listItems: { eventId: string }[],
    scrollbackState: ScrollbackState,
    onFirstMessageReached: (watermark: bigint) => void,
) => {
    const watermark = scrollbackState?.fromInclusiveMiniblockNum
    const terminus = scrollbackState?.terminus
    useEffect(() => {
        if (highlightId && watermark && !terminus) {
            const found = listItems.some((s) => s.eventId === highlightId)
            if (!found) {
                // note: the callback can be invoked repeatedly but will only
                // have an effect if the watermark changes
                onFirstMessageReached(watermark)
            }
        }
    }, [highlightId, listItems, onFirstMessageReached, terminus, watermark])
}

type MessageLink =
    | { type: 'same-channel-message'; focusMessage: () => void }
    | { type: 'internal-link'; path: string }
    | {
          type: 'external'
          link: string
      }
    | { type: undefined; link: undefined }

// Suggested usage:
// If its on the same channel, focus the message
// If its in a different channel, change the route
// If its a external link, open it in a new tab
export const useMessageLink = (href: string | undefined): MessageLink => {
    const location = useLocation()
    const { refocusItem } = useFocusMessage((s) => s.actions)

    return useMemo(() => {
        if (!href) {
            return { type: undefined, link: undefined }
        }

        let url = null

        // @miguel-nascimento 2025-09-09
        // This is to handle the case where the user clicks on a mention of an address
        // It was incorreclty being parsed as a mailto link.
        // It should be handled in a different place. That's the quickfix.
        if (href.startsWith('mailto:')) {
            const remaining = href.slice(7)
            if (remaining.match(USER_ADDRESS_MENTION_REGEX)) {
                return { type: undefined, link: undefined }
            }
        }

        try {
            // Handle relative URLs by making them absolute
            const absoluteHref = href.startsWith('/') ? `${window.location.origin}${href}` : href
            url = new URL(absoluteHref)
        } catch (_error) {
            return { type: undefined, link: undefined }
        }

        if (url.hostname !== window.location.hostname && !isTownsAppUrl(url)) {
            return { type: 'external', link: href }
        }

        // remove the / from the end of the pathname
        const locationPathname = location.pathname.endsWith('/')
            ? location.pathname.slice(0, -1)
            : location.pathname
        const urlPathname = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname

        if (urlPathname === locationPathname && url.hash) {
            const messageId = url.hash.slice(1) // remove the # from the hash
            return {
                type: 'same-channel-message',
                focusMessage: () => {
                    refocusItem(messageId, 'forced')
                },
            }
        }

        return { type: 'internal-link', path: url.pathname + url.hash }
    }, [href, location.pathname, refocusItem])
}
